import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  expandToWords,
  selectedLength,
  snapToAtoms,
  spanOf,
  wholeSpan,
  type Span,
} from '../lib/selection.ts';
import { formatPickedCount } from '../lib/format.ts';
import './TextSelection.css';

/**
 * How far above the caret the finger sits when it is holding a handle. The dot
 * is drawn off the line by this much (see the CSS), so sampling at the finger
 * itself would read the line above or below the one being pointed at.
 */
const LIFT = 14;

/**
 * How long a handle has to stop moving before it starts refining by character.
 *
 * The handoff asks for words first and characters after, and a pause is what
 * separates the two intents: a drag across a sentence is aiming at a word, a
 * drag that lingers is aiming between two letters. Time rather than distance —
 * a long word and a short one are the same gesture at different scales.
 */
const REFINE_MS = 400;

/** A caret position, in the coordinates of the layer this draws into. */
interface Mark {
  left: number;
  top: number;
  height: number;
}

function textNodesIn(element: Element): Text[] {
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) nodes.push(node as Text);
  return nodes;
}

/** Every atom TextBody drew, with the place in the body it came from. */
function atomsIn(root: HTMLElement) {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-at]')).map((element) => ({
    element,
    at: Number(element.dataset.at ?? 0),
    length: Number(element.dataset.len ?? 0),
  }));
}

/**
 * The caret under a point, from whichever of the two spellings this engine has.
 * WebKit shipped `caretRangeFromPoint` years before the standard one, and it is
 * still the only one on the iPhones this runs on.
 */
function caretAt(x: number, y: number): { node: Node; offset: number } | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const position = doc.caretPositionFromPoint?.(x, y);
  if (position) return { node: position.offsetNode, offset: position.offset };
  const range = doc.caretRangeFromPoint?.(x, y);
  return range ? { node: range.startContainer, offset: range.startOffset } : null;
}

/**
 * A point on screen, read back as an index into the body.
 *
 * The atoms carry their own offsets, so this only has to find which one was hit
 * and how far into its text nodes the caret landed — the marks and the selection
 * spans TextBody wraps runs in never enter the arithmetic.
 */
export function indexAtPoint(root: HTMLElement, body: string, x: number, y: number): number | null {
  const caret = caretAt(x, y);
  const node = caret?.node ?? null;
  const element = node?.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element | null);
  const holder = element?.closest<HTMLElement>('[data-at]') ?? null;

  if (!holder || !root.contains(holder)) {
    // Past the end of the last line, or off the side of the bubble: the nearest
    // end is what the finger meant, and it is better than dropping the move.
    const box = root.getBoundingClientRect();
    if (y > box.bottom || (y > box.top && x > box.right)) return body.length;
    if (y < box.top || x < box.left) return 0;
    return null;
  }

  const at = Number(holder.dataset.at ?? 0);
  const length = Number(holder.dataset.len ?? 0);
  // A smiley and an address are one thing each: which side of the middle the
  // finger is on is the whole question.
  if (holder.tagName === 'IMG' || !caret || node?.nodeType !== Node.TEXT_NODE) {
    const box = holder.getBoundingClientRect();
    return x > box.left + box.width / 2 ? at + length : at;
  }

  let before = 0;
  for (const text of textNodesIn(holder)) {
    if (text === node) return at + before + caret.offset;
    before += text.data.length;
  }
  return at;
}

/** Where the caret at one index is, in the layer's coordinates. */
function markAt(root: HTMLElement, origin: DOMRect, index: number): Mark | null {
  const atoms = atomsIn(root);
  const atom =
    atoms.find((item) => index >= item.at && index <= item.at + item.length) ??
    atoms[atoms.length - 1];
  if (!atom) return null;

  if (atom.element.tagName === 'IMG') {
    const box = atom.element.getBoundingClientRect();
    const edge = index <= atom.at ? box.left : box.right;
    return { left: edge - origin.left, top: box.top - origin.top, height: box.height };
  }

  let local = index - atom.at;
  const range = document.createRange();
  for (const text of textNodesIn(atom.element)) {
    if (local <= text.data.length) {
      range.setStart(text, local);
      range.collapse(true);
      const box = range.getClientRects()[0] ?? range.getBoundingClientRect();
      return { left: box.left - origin.left, top: box.top - origin.top, height: box.height };
    }
    local -= text.data.length;
  }

  const box = atom.element.getBoundingClientRect();
  return { left: box.right - origin.left, top: box.top - origin.top, height: box.height };
}

/**
 * The handles around a selected fragment and the bar of things to do with it.
 *
 * Drawn over the bubble rather than left to the browser: `user-select` is off on
 * a bubble because a press-and-hold is the menu's gesture, and turning it back
 * on would bring the system callout with it — which cannot be suppressed in a
 * WKWebView from here. The rest of the argument is in lib/selection.ts.
 */
export function TextSelection({
  root,
  body,
  span,
  onChange,
  onCopy,
  onQuote,
  onClose,
}: {
  /** The element TextBody was rendered into — what gets measured and hit-tested. */
  root: HTMLElement | null;
  body: string;
  span: Span;
  onChange: (span: Span) => void;
  onCopy: () => void;
  onQuote: () => void;
  onClose: () => void;
}) {
  const layer = useRef<HTMLDivElement | null>(null);
  const [head, setHead] = useState<Mark | null>(null);
  const [tail, setTail] = useState<Mark | null>(null);
  /** Which handle is moving, what the other end is pinned to, how fine it is. */
  const drag = useRef<{ anchor: number; grain: 'word' | 'char' } | null>(null);
  const refine = useRef<number | null>(null);

  const measure = useCallback(() => {
    const box = layer.current?.getBoundingClientRect();
    if (!root || !box) return;
    setHead(markAt(root, box, span.start));
    setTail(markAt(root, box, span.end));
  }, [root, span.start, span.end]);

  useLayoutEffect(measure, [measure, body]);

  // The bubble moves when the keyboard opens or the window turns; the handles
  // are absolute and would stay where the text used to be.
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // A press anywhere else puts the handles away, as the handoff asks. On
    // pointerdown rather than click: the next thing that press does may be a
    // drag through another message, and that message has to find the mode
    // already closed rather than fight it for the same finger.
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (layer.current?.contains(target) || root?.contains(target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
    };
  }, [onClose, root]);

  useEffect(() => () => window.clearTimeout(refine.current ?? undefined), []);

  const startDrag = (edge: 'start' | 'end') => (event: ReactPointerEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { anchor: edge === 'start' ? span.end : span.start, grain: 'word' };
    window.clearTimeout(refine.current ?? undefined);
    refine.current = window.setTimeout(() => {
      if (drag.current) drag.current.grain = 'char';
    }, REFINE_MS);
  };

  const moveDrag = (edge: 'start' | 'end') => (event: ReactPointerEvent<HTMLSpanElement>) => {
    const state = drag.current;
    if (!state || !root) return;
    event.preventDefault();

    const index = indexAtPoint(root, body, event.clientX, event.clientY + (edge === 'start' ? LIFT : -LIFT));
    if (index === null) return;

    const next = spanOf(state.anchor, index);
    onChange(state.grain === 'word' ? expandToWords(body, next) : snapToAtoms(body, next));

    // Every move puts the refining back out of reach: it is a pause that asks
    // for characters, and a drag still travelling has not paused.
    state.grain = 'word';
    window.clearTimeout(refine.current ?? undefined);
    refine.current = window.setTimeout(() => {
      if (drag.current) drag.current.grain = 'char';
    }, REFINE_MS);
  };

  const endDrag = () => {
    drag.current = null;
    window.clearTimeout(refine.current ?? undefined);
  };

  const handle = (edge: 'start' | 'end', mark: Mark | null) =>
    mark && (
      <span
        className={`selection-handle selection-handle-${edge}`}
        style={{ left: mark.left, top: mark.top, height: mark.height }}
        onPointerDown={startDrag(edge)}
        onPointerMove={moveDrag(edge)}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        aria-hidden="true"
      />
    );

  return (
    <div className="selection-layer" ref={layer}>
      {handle('start', head)}
      {handle('end', tail)}

      {head && (
        <div
          className="selection-bar"
          style={{ left: head.left, top: head.top }}
          role="toolbar"
          aria-label={formatPickedCount(selectedLength(body, span))}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button type="button" className="selection-action" onClick={onCopy}>
            Копировать
          </button>
          <button type="button" className="selection-action" onClick={onQuote}>
            Цитировать
          </button>
          <button
            type="button"
            className="selection-action"
            onClick={() => onChange(wholeSpan(body))}
          >
            Всё
          </button>
        </div>
      )}
    </div>
  );
}
